import { isWebsiteBuilderParentNode, TWebsiteBuilderNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNode";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { applyHistoryChange, applyUndoHistoryChange, isWebsiteBuilderEditorHistoryAction, TWebsiteBuilderEditorAction, TWebsiteBuilderEditorHistoryAction, WebsiteBuilderEditorActionType } from "redux/actions/websiteBuilder/editor/websiteBuilderEditorAction";
import { IWebsiteBuilderFont, IWebsiteBuilderWebsiteVersionGlobalStyles, IWebsiteBuilderMediaItem, IWebsiteBuilderTheme, IWebsiteBuilderWebsiteVersion, TWebsiteBuilderWebsiteVersionNavigationMenuItem, TWebsiteBuilderWebsiteVersionPage, IWebsiteBuilderIconAsset } from "../models/websiteBuilder";
import { cloneDeep, uniqBy } from "lodash";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { TWebsiteBuilderSelectedNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSelectedNode";
import { TWebsiteBuilderDevice } from "pages/secure/facility/websiteBuilder/editor/website/shared/device";
import { TWebsiteBuilderRootCSSVariables, websiteBuilderGenerateRootCssVariables } from "pages/secure/facility/websiteBuilder/editor/website/shared/rootCssVariables";
import { IFormBuilderForm } from "pages/secure/facility/websiteBuilder/editor/formBuilder/editor/model/formBuilderForm";

type TWebsiteBuilderPageMap = Map<number, TWebsiteBuilderWebsiteVersionPage>;
export type TWebsiteBuilderActivePageNodeMap = Map<string, TWebsiteBuilderNode>;
export type TWebsiteBuilderMode = "design" | "preview";
export type TWebsiteBuilderEditorLoadingStatus = "standby" | "loading" | "failed" | "succeeded";
export type TWebsiteBuilderAutoSaveStatus = "unsaved-changes" | "saving" | "error" | "saved";
type TWebsiteBuilderPagesAutoSaveStatus = Record<number, TWebsiteBuilderAutoSaveStatus>;

interface IWebsiteBuilderNoneFatalError {
  type: "none";
}

export interface IWebsiteBuilderMigrationFatalError {
  type: "migration";
  message: string;
}

export type TWebsiteBuilderFatalError = IWebsiteBuilderNoneFatalError | IWebsiteBuilderMigrationFatalError;

interface IWebsiteBuilderAutoSaveStatusState {
  theme: TWebsiteBuilderAutoSaveStatus;
  globalStyles: TWebsiteBuilderAutoSaveStatus;
  globalLayout: TWebsiteBuilderAutoSaveStatus;
  pages: TWebsiteBuilderPagesAutoSaveStatus;
  combined: TWebsiteBuilderAutoSaveStatus;
}

export interface IWebsiteBuilderEditor {
  websiteVersion: IWebsiteBuilderWebsiteVersion;
  viewingPastVersion: boolean;
  deviceView: TWebsiteBuilderDevice;
  mode: TWebsiteBuilderMode;
  header: IWebsiteBuilderHeader;
  footer: IWebsiteBuilderFooter;
  pages: TWebsiteBuilderWebsiteVersionPage[];
  pageMap: TWebsiteBuilderPageMap;
  activePageId: number;
  activePageNodeMap: TWebsiteBuilderActivePageNodeMap;
  navigationMenuItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[];
  history: TWebsiteBuilderEditorHistoryAction[];
  historyIndex: number;
  selectedNode: TWebsiteBuilderSelectedNode;
  theme: IWebsiteBuilderTheme;
  availableThemes: IWebsiteBuilderTheme[];
  globalStyles: IWebsiteBuilderWebsiteVersionGlobalStyles;
  fonts: IWebsiteBuilderFont[];
  iconAssets: IWebsiteBuilderIconAsset[];
  suggestedIconAssets: IWebsiteBuilderIconAsset[];
  media: IWebsiteBuilderMediaItem[];
  stockMedia: IWebsiteBuilderMediaItem[];
  forms: IFormBuilderForm[];
  rootCssVariables: TWebsiteBuilderRootCSSVariables;
  editorLoadingStatus: TWebsiteBuilderEditorLoadingStatus;
  autoSaveStatusState: IWebsiteBuilderAutoSaveStatusState;
  fatalError: TWebsiteBuilderFatalError;
}

function generatePageMap(pages: TWebsiteBuilderWebsiteVersionPage[]): TWebsiteBuilderPageMap {
  const map: TWebsiteBuilderPageMap = new Map();

  if (pages == null || pages.length === 0) {
    return map;
  }

  for (const page of pages) {
    map.set(page.id, page);
  }

  return map;
}

function generateActivePageNodeMap(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
): TWebsiteBuilderActivePageNodeMap {
  const map: TWebsiteBuilderActivePageNodeMap = new Map();
  
  if (header == null || root == null || footer == null) {
    return map;
  }

  function populateMap(node: TWebsiteBuilderNode): void {
    map.set(node.id, node);

    if (isWebsiteBuilderParentNode(node)) {
      if (node.type === "root") {
        populateMap(header);
      }

      node.children.forEach(child => {
        populateMap(child);
      });

      if (node.type === "root") {
        populateMap(footer);
      }
    }
  }

  populateMap(root);

  return map;
}

export const websiteBuilderInitialEditor: IWebsiteBuilderEditor = {
  websiteVersion: null,
  viewingPastVersion: false,
  deviceView: "desktop",
  mode: "design",
  header: null,
  footer: null,
  pages: [],
  pageMap: generatePageMap([]),
  activePageId: null,
  activePageNodeMap: generateActivePageNodeMap(null, null, null),
  navigationMenuItems: [],
  history: [],
  historyIndex: -1,
  selectedNode: null,
  theme: null,
  availableThemes: [],
  globalStyles: null,
  fonts: [],
  iconAssets: [],
  suggestedIconAssets: [],
  media: [],
  stockMedia: [],
  forms: [],
  rootCssVariables: null,
  editorLoadingStatus: "standby",
  autoSaveStatusState: {
    theme: "saved",
    globalStyles: "saved",
    globalLayout: "saved",
    pages: {},
    combined: "saved",
  },
  fatalError: { type: "none" },
};

function containsSelectedNode(node: TWebsiteBuilderNode, selectedNodeId: string): boolean {
  if (node == null || selectedNodeId == null) {
    return false;
  }

  if (node.id === selectedNodeId || node.type === "root") {
    return true;
  }

  if (isWebsiteBuilderParentNode(node)) {
    return node.children.some(child => containsSelectedNode(child, selectedNodeId));
  } else {
    return false;
  }
}

function handleHistoryAction(
  editor: IWebsiteBuilderEditor,
  root: IWebsiteBuilderRoot,
  action: TWebsiteBuilderEditorHistoryAction,
): IWebsiteBuilderEditor {
  if (action.type === WebsiteBuilderEditorActionType.UNDO) {
    const actionToUndo = editor.history[editor.historyIndex];

    let updatedSelectedNode = editor.selectedNode;

    if (
      (
        actionToUndo.type === WebsiteBuilderEditorActionType.ADD_NODE &&
        containsSelectedNode(actionToUndo.payload.snapshot.node, editor.selectedNode?.id)
      ) || (
        actionToUndo.type === WebsiteBuilderEditorActionType.ADD_TAB &&
        containsSelectedNode(actionToUndo.payload.tab, editor.selectedNode?.id)
      )
    ) {
      updatedSelectedNode = null;
    } else if (actionToUndo.type === WebsiteBuilderEditorActionType.MOVE_ELEMENT && actionToUndo.payload.id === editor.selectedNode?.id) {
      updatedSelectedNode = {
        type: "element",
        id: actionToUndo.payload.id,
        sectionId: actionToUndo.payload.prevSectionId,
        containerId: actionToUndo.payload.prevContainerId,
        gridCellId: actionToUndo.payload.prevGridCellId,
        tabId: actionToUndo.payload.prevTabId,
        editingMode: {
          type: "none",
        },
      };
    } else if (actionToUndo.type === WebsiteBuilderEditorActionType.MOVE_CONTAINER && actionToUndo.payload.id === editor.selectedNode?.id) {
      updatedSelectedNode = {
        type: "container",
        id: actionToUndo.payload.id,
        sectionId: actionToUndo.payload.prevSectionId,
        editingMode: {
          type: "none",
        },
      };
    } else if (
      actionToUndo.type === WebsiteBuilderEditorActionType.SET_HIDEABLE_NODE_VISIBILITY &&
      containsSelectedNode(editor.activePageNodeMap.get(actionToUndo.payload.id), editor.selectedNode?.id) &&
      actionToUndo.payload.visible
    ) {
      updatedSelectedNode = null;
    } else if (actionToUndo.type === WebsiteBuilderEditorActionType.SWAP_GRID_CELL_CONTENT) {
      const selectedPrev = containsSelectedNode(editor.activePageNodeMap.get(actionToUndo.payload.prevGridCellId), editor.selectedNode?.id);
      const selectedNext = containsSelectedNode(editor.activePageNodeMap.get(actionToUndo.payload.nextGridCellId), editor.selectedNode?.id);

      if (selectedPrev || selectedNext) {
        updatedSelectedNode = {
          type: "element",
          id: editor.selectedNode.id,
          sectionId: actionToUndo.payload.sectionId,
          containerId: actionToUndo.payload.gridId,
          gridCellId: selectedPrev ? actionToUndo.payload.nextGridCellId : actionToUndo.payload.prevGridCellId,
          tabId: null,
          editingMode: {
            type: "none",
          },
        };
      }
    }

    return {
      ...editor,
      ...applyUndoHistoryChange(editor.header, root, editor.footer, editor.pages, editor.activePageId, actionToUndo),
      historyIndex: editor.historyIndex - 1,
      selectedNode: updatedSelectedNode,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.REDO) {
    const nextActionIndex = editor.historyIndex + 1;
    const actionToRedo = editor.history[nextActionIndex];

    let updatedSelectedNode = editor.selectedNode;

    if (
      (
        actionToRedo.type === WebsiteBuilderEditorActionType.DELETE_NODE &&
        containsSelectedNode(actionToRedo.payload.snapshot.node, editor.selectedNode?.id)
      ) || (
        actionToRedo.type === WebsiteBuilderEditorActionType.DELETE_TAB &&
        containsSelectedNode(actionToRedo.payload.tab, editor.selectedNode?.id)
      )
    ) {
      updatedSelectedNode = null;
    } else if (actionToRedo.type === WebsiteBuilderEditorActionType.MOVE_ELEMENT && actionToRedo.payload.id === editor.selectedNode?.id) {
      updatedSelectedNode = {
        type: "element",
        id: actionToRedo.payload.id,
        sectionId: actionToRedo.payload.nextSectionId,
        containerId: actionToRedo.payload.nextContainerId,
        gridCellId: actionToRedo.payload.nextGridCellId,
        tabId: actionToRedo.payload.nextTabId,
        editingMode: {
          type: "none",
        },
      };
    } else if (actionToRedo.type === WebsiteBuilderEditorActionType.MOVE_CONTAINER && actionToRedo.payload.id === editor.selectedNode?.id) {
      updatedSelectedNode = {
        type: "container",
        id: actionToRedo.payload.id,
        sectionId: actionToRedo.payload.nextSectionId,
        editingMode: {
          type: "none",
        },
      };
    } else if (
      actionToRedo.type === WebsiteBuilderEditorActionType.SET_HIDEABLE_NODE_VISIBILITY &&
      containsSelectedNode(editor.activePageNodeMap.get(actionToRedo.payload.id), editor.selectedNode?.id) &&
      !actionToRedo.payload.visible
    ) {
      updatedSelectedNode = null;
    } else if (actionToRedo.type === WebsiteBuilderEditorActionType.SWAP_GRID_CELL_CONTENT) {
      const selectedPrev = containsSelectedNode(editor.activePageNodeMap.get(actionToRedo.payload.prevGridCellId), editor.selectedNode?.id);
      const selectedNext = containsSelectedNode(editor.activePageNodeMap.get(actionToRedo.payload.nextGridCellId), editor.selectedNode?.id);

      if (selectedPrev || selectedNext) {
        updatedSelectedNode = {
          type: "element",
          id: editor.selectedNode.id,
          sectionId: actionToRedo.payload.sectionId,
          containerId: actionToRedo.payload.gridId,
          gridCellId: selectedPrev ? actionToRedo.payload.nextGridCellId : actionToRedo.payload.prevGridCellId,
          tabId: null,
          editingMode: {
            type: "none",
          },
        };
      }
    }

    return {
      ...editor,
      ...applyHistoryChange(editor.header, root, editor.footer, editor.pages, editor.activePageId, actionToRedo),
      historyIndex: nextActionIndex,
      selectedNode: updatedSelectedNode,
    };
  }

  let updatedHistory = editor.history;

  const canRedo = editor.history.length > 0 && editor.historyIndex < editor.history.length - 1;

  if (canRedo) {
    updatedHistory = editor.history.slice(0, editor.historyIndex + 1);
  }

  let updatedSelectedNode: TWebsiteBuilderSelectedNode = editor.selectedNode;

  if (
    (
      action.type === WebsiteBuilderEditorActionType.DELETE_NODE &&
      containsSelectedNode(action.payload.snapshot.node, editor.selectedNode?.id)
    ) || (
      action.type === WebsiteBuilderEditorActionType.DELETE_TAB &&
      containsSelectedNode(action.payload.tab, editor.selectedNode?.id)
    )
  ) {
    updatedSelectedNode = null;
  } else if (
    action.type === WebsiteBuilderEditorActionType.RESIZE_ELEMENT ||
    action.type === WebsiteBuilderEditorActionType.RESIZE_CONTAINER ||
    action.type === WebsiteBuilderEditorActionType.RESIZE_SECTION
  ) {
    updatedSelectedNode = {
      ...editor.selectedNode,
      editingMode: {
        type: "none",
      },
    };
  } else if (action.type === WebsiteBuilderEditorActionType.MOVE_ELEMENT) {
    updatedSelectedNode = {
      type: "element",
      id: action.payload.id,
      sectionId: action.payload.nextSectionId,
      containerId: action.payload.nextContainerId,
      gridCellId: action.payload.nextGridCellId,
      tabId: action.payload.nextTabId,
      editingMode: {
        type: "none",
      },
    };
  } else if (action.type === WebsiteBuilderEditorActionType.MOVE_CONTAINER) {
    updatedSelectedNode = {
      type: "container",
      id: action.payload.id,
      sectionId: action.payload.nextSectionId,
      editingMode: {
        type: "none",
      },
    };
  } else if (
    action.type === WebsiteBuilderEditorActionType.SET_HIDEABLE_NODE_VISIBILITY &&
    containsSelectedNode(editor.activePageNodeMap.get(action.payload.id), editor.selectedNode?.id) &&
    !action.payload.visible
  ) {
    updatedSelectedNode = null;
  } else if (action.type === WebsiteBuilderEditorActionType.SWAP_GRID_CELL_CONTENT) {
    updatedSelectedNode = {
      type: "element",
      id: action.payload.elementId,
      sectionId: action.payload.sectionId,
      containerId: action.payload.gridId,
      gridCellId: action.payload.nextGridCellId,
      tabId: null,
      editingMode: {
        type: "none",
      },
    };
  }

  const updatedEditor: IWebsiteBuilderEditor = {
    ...editor,
    ...applyHistoryChange(editor.header, root, editor.footer, editor.pages, editor.activePageId, action),
    history: [...updatedHistory, action],
    historyIndex: editor.historyIndex + 1,
    selectedNode: updatedSelectedNode,
  };

  return updatedEditor;
}

function initializeAutoSaveStatusStatePages(prev: IWebsiteBuilderAutoSaveStatusState, pages: TWebsiteBuilderWebsiteVersionPage[]) {
  const autoSaveStatusState: IWebsiteBuilderAutoSaveStatusState = {
    ...cloneDeep(prev),
    pages: {},
  };

  for (const page of pages) {
    autoSaveStatusState.pages[page.id] = "saved";
  }

  return autoSaveStatusState;
}

interface IPageNavigationMenuItemStructure {
  id: number;
  page_id: number;
}

function pageNavigationMenuItemStructuresChanged(
  prevNavigationMenuItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[],
  nextNavigationMenuItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[],
) {
  const getStructures = (items: TWebsiteBuilderWebsiteVersionNavigationMenuItem[]): IPageNavigationMenuItemStructure[] => {
    const structures: IPageNavigationMenuItemStructure[] = [];

    function traverse(navigationMenuItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[]) {
      for (const item of navigationMenuItems) {
        if (item.type === "page") {
          structures.push({ id: item.id, page_id: item.page_id });
        } else if (item.type === "dropdown") {
          traverse(item.children);
        }
      }
    }

    traverse(items);

    return structures.sort((prev, next) => prev.id - next.id);
  };

  const prevStructures = getStructures(prevNavigationMenuItems);
  const nextStructures = getStructures(nextNavigationMenuItems);

  return (
    prevStructures.length !== nextStructures.length ||
    prevStructures.some((prevStructure, index) => {
      const nextStructure = nextStructures[index];

      return prevStructure.id !== nextStructure.id || prevStructure.page_id !== nextStructure.page_id;
    })
  );
}

interface IReconcileRetrievedPagesUpdate {
  pages: IWebsiteBuilderEditor["pages"];
  pageMap: IWebsiteBuilderEditor["pageMap"];
  activePageId: IWebsiteBuilderEditor["activePageId"];
  activePageNodeMap: IWebsiteBuilderEditor["activePageNodeMap"];
  selectedNode: IWebsiteBuilderEditor["selectedNode"];
  history: IWebsiteBuilderEditor["history"];
  historyIndex: IWebsiteBuilderEditor["historyIndex"];
  autoSaveStatusState: IWebsiteBuilderEditor["autoSaveStatusState"];
}

function reconcileRetrievedPages(
  prevPages: TWebsiteBuilderWebsiteVersionPage[],
  nextPages: TWebsiteBuilderWebsiteVersionPage[],
  activePageId: number,
  header: IWebsiteBuilderHeader,
  footer: IWebsiteBuilderFooter,
  autoSaveStatusState: IWebsiteBuilderAutoSaveStatusState,
): IReconcileRetrievedPagesUpdate {
  const possiblePrevSlug = prevPages.find(prevPage => prevPage.id === activePageId)?.slug;

  const nextActivePage =
    nextPages.find(nextPage => nextPage.id === activePageId) ??
    nextPages.find(nextPage => nextPage.slug === possiblePrevSlug) ??
    nextPages.find(nextPage => nextPage.is_home) ??
    nextPages[0];
  
  return {
    pages: nextPages,
    pageMap: generatePageMap(nextPages),
    activePageId: nextActivePage.id,
    activePageNodeMap: generateActivePageNodeMap(header, nextActivePage.root, footer),
    history: [],
    historyIndex: -1,
    selectedNode: null,
    autoSaveStatusState: initializeAutoSaveStatusStatePages(autoSaveStatusState, nextPages),
  };
}

export function websiteBuilderEditorReducer(editor: IWebsiteBuilderEditor, action: TWebsiteBuilderEditorAction): IWebsiteBuilderEditor {  
  if (action.type === WebsiteBuilderEditorActionType.START_LOADING_EDITOR) {
    return {
      ...editor,
      editorLoadingStatus: "loading",
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.LOAD_EDITOR) {
    if (action.payload.editorLoadingStatus === "failed") {
      return {
        ...editor,
        editorLoadingStatus: action.payload.editorLoadingStatus,
      };
    } else {
      const {
        websiteVersion,
        viewingPastVersion,
        header,
        footer,
        pages,
        activePageId,
        navigationMenuItems,
        theme,
        availableThemes,
        globalStyles,
        fonts,
        iconAssets,
        suggestedIconAssets,
        media,
        stockMedia,
        editorLoadingStatus,
        forms,
      } = action.payload;

      const autoSaveStatusState = initializeAutoSaveStatusStatePages(editor.autoSaveStatusState, pages);

      const pageMap = generatePageMap(pages);

      const root = pageMap.get(activePageId).root;

      return {
        ...editor,
        websiteVersion,
        viewingPastVersion,
        mode: viewingPastVersion ? "preview" : editor.mode,
        header,
        footer,
        pages,
        pageMap,
        activePageId,
        activePageNodeMap: generateActivePageNodeMap(header, root, footer),
        navigationMenuItems,
        theme,
        availableThemes,
        globalStyles,
        fonts,
        iconAssets,
        suggestedIconAssets,
        media,
        stockMedia,
        rootCssVariables: websiteBuilderGenerateRootCssVariables(theme, globalStyles, fonts),
        editorLoadingStatus,
        autoSaveStatusState,
        forms,
      };
    }
  }
  
  if (action.type === WebsiteBuilderEditorActionType.EXIT_EDITOR) {
    return cloneDeep(websiteBuilderInitialEditor);
  }

  if (action.type === WebsiteBuilderEditorActionType.SET_FATAL_ERROR) {
    return {
      ...editor,
      fatalError: action.payload.fatalError,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.UPDATE_AUTO_SAVE_STATUS_STATE) {
    const { theme, globalStyles, globalLayout, page } = action.payload;

    const updatedAutoSaveStatusState: IWebsiteBuilderAutoSaveStatusState = {
      theme: theme ?? editor.autoSaveStatusState.theme,
      globalStyles: globalStyles ?? editor.autoSaveStatusState.globalStyles,
      globalLayout: globalLayout ?? editor.autoSaveStatusState.globalLayout,
      pages: {
        ...editor.autoSaveStatusState.pages,
        ...(page ? { [page.id]: page.status } : {}),
      },
      combined: editor.autoSaveStatusState.combined,
    };

    if (
      updatedAutoSaveStatusState.theme === "saving" ||
      updatedAutoSaveStatusState.globalStyles === "saving" ||
      updatedAutoSaveStatusState.globalLayout === "saving" ||
      Object.values(updatedAutoSaveStatusState.pages).includes("saving")
    ) {
      updatedAutoSaveStatusState.combined = "saving";
    } else if (
      updatedAutoSaveStatusState.theme === "unsaved-changes" ||
      updatedAutoSaveStatusState.globalStyles === "unsaved-changes" ||
      updatedAutoSaveStatusState.globalLayout === "unsaved-changes" ||
      Object.values(updatedAutoSaveStatusState.pages).includes("unsaved-changes")
    ) {
      updatedAutoSaveStatusState.combined = "unsaved-changes";
    } else if (
      updatedAutoSaveStatusState.theme === "error" ||
      updatedAutoSaveStatusState.globalStyles === "error" ||
      updatedAutoSaveStatusState.globalLayout === "error" ||
      Object.values(updatedAutoSaveStatusState.pages).includes("error")
    ) {
      updatedAutoSaveStatusState.combined = "error";
    } else {
      updatedAutoSaveStatusState.combined = "saved";
    }
    
    return {
      ...editor,
      autoSaveStatusState: updatedAutoSaveStatusState,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.SET_DEVICE_VIEW) {
    return {
      ...editor,
      deviceView: action.payload.deviceView,
      selectedNode: null,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.SET_MODE) {
    return {
      ...editor,
      mode: editor.viewingPastVersion ? "preview" : action.payload.mode,
      selectedNode: null,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.PUBLISH_WEBSITE_VERSION) {
    const { websiteVersion, globalStyles, header, footer, pages, navigationMenuItems } = action.payload;
    
    return {
      ...editor,
      websiteVersion,
      globalStyles,
      header,
      footer,
      rootCssVariables: websiteBuilderGenerateRootCssVariables(editor.theme, globalStyles, editor.fonts),
      navigationMenuItems,
      ...reconcileRetrievedPages(editor.pages, pages, editor.activePageId, header, footer, editor.autoSaveStatusState),
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.RESTORE_WEBSITE_VERSION) {
    const { websiteVersion, globalStyles, header, footer, pages, navigationMenuItems } = action.payload;

    return {
      ...editor,
      mode: "design",
      viewingPastVersion: false,
      websiteVersion,
      globalStyles,
      header,
      footer,
      rootCssVariables: websiteBuilderGenerateRootCssVariables(editor.theme, globalStyles, editor.fonts),
      navigationMenuItems,
      ...reconcileRetrievedPages(editor.pages, pages, editor.activePageId, header, footer, editor.autoSaveStatusState),
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.SELECT_PAGE) {
    const { activePageId } = action.payload;

    const root = editor.pageMap.get(activePageId)?.root;

    if (root == null) {
      return editor;
    }

    return {
      ...editor,
      activePageId,
      activePageNodeMap: generateActivePageNodeMap(editor.header, root, editor.footer),
      selectedNode: null,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.ADD_PAGE) {
    const { navigationMenuItems, pages, page } = action.payload;
    
    return {
      ...editor,
      navigationMenuItems,
      ...reconcileRetrievedPages(editor.pages, pages, page.id, editor.header, editor.footer, editor.autoSaveStatusState),
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.DELETE_PAGE) {
    const { navigationMenuItems, pages } = action.payload;

    return {
      ...editor,
      navigationMenuItems,
      ...reconcileRetrievedPages(editor.pages, pages, editor.activePageId, editor.header, editor.footer, editor.autoSaveStatusState),
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.RENAME_PAGE) {
    const { page } = action.payload;

    const updatedPages: TWebsiteBuilderWebsiteVersionPage[] = [];

    for (const editorPage of editor.pages) {
      if (page.id === editorPage.id) {
        updatedPages.push(page);
      } else {
        updatedPages.push(editorPage);
      }
    }

    const pageMap = generatePageMap(updatedPages);

    const root = pageMap.get(editor.activePageId).root;

    return {
      ...editor,
      pages: updatedPages,
      pageMap,
      activePageNodeMap: generateActivePageNodeMap(editor.header, root, editor.footer),
      history: [],
      historyIndex: -1,
      selectedNode: null,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.SET_HOME_PAGE) {
    const { navigationMenuItems, pages } = action.payload;
    
    return {
      ...editor,
      navigationMenuItems,
      ...reconcileRetrievedPages(editor.pages, pages, editor.activePageId, editor.header, editor.footer, editor.autoSaveStatusState),
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.REORDER_NAVIGATION_MENU_ITEMS) {
    const { navigationMenuItems, pages } = action.payload;

    if (pageNavigationMenuItemStructuresChanged(editor.navigationMenuItems, navigationMenuItems)) {
      return {
        ...editor,
        navigationMenuItems,
        ...reconcileRetrievedPages(editor.pages, pages, editor.activePageId, editor.header, editor.footer, editor.autoSaveStatusState),
      };
    } else {
      return {
        ...editor,
        navigationMenuItems,
      };
    }
  }

  if (action.type === WebsiteBuilderEditorActionType.UPDATE_NAVIGATION_MENU_ITEM) {
    const { navigationMenuItem } = action.payload;

    const updatedNavigationMenuItems = editor.navigationMenuItems.map(editorNavigationMenuItem => {
      if (navigationMenuItem.id === editorNavigationMenuItem.id) {
        return navigationMenuItem;
      } else {
        if (editorNavigationMenuItem.type !== "dropdown" || navigationMenuItem.type === "dropdown") {
          return editorNavigationMenuItem;
        } else {
          let childUpdated = false;

          const updatedChildren = editorNavigationMenuItem.children.map(child => {
            if (child.id !== navigationMenuItem.id) {
              return child;
            } else {
              childUpdated = true;
              return navigationMenuItem;
            }
          });

          if (!childUpdated) {
            return editorNavigationMenuItem;
          } else {
            return {
              ...editorNavigationMenuItem,
              children: updatedChildren,
            };
          }
        }
      }
    });

    return {
      ...editor,
      navigationMenuItems: updatedNavigationMenuItems,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.ADD_NAVIGATION_MENU_ITEM) {
    const { navigationMenuItems, pages } = action.payload;

    if (pageNavigationMenuItemStructuresChanged(editor.navigationMenuItems, navigationMenuItems)) {
      return {
        ...editor,
        navigationMenuItems,
        ...reconcileRetrievedPages(editor.pages, pages, editor.activePageId, editor.header, editor.footer, editor.autoSaveStatusState),
      };
    } else {
      return {
        ...editor,
        navigationMenuItems,
      };
    }
  }

  if (action.type === WebsiteBuilderEditorActionType.DELETE_NAVIGATION_MENU_ITEM) {
    const { navigationMenuItems, pages } = action.payload;

    if (pageNavigationMenuItemStructuresChanged(editor.navigationMenuItems, navigationMenuItems)) {
      return {
        ...editor,
        navigationMenuItems,
        ...reconcileRetrievedPages(editor.pages, pages, editor.activePageId, editor.header, editor.footer, editor.autoSaveStatusState),
      };
    } else {
      return {
        ...editor,
        navigationMenuItems,
      };
    }
  }

  if (action.type === WebsiteBuilderEditorActionType.SELECT_THEME) {
    const foundTheme = editor.availableThemes.find(theme => theme.id === action.payload.id);

    if (!foundTheme) {
      return editor;
    }

    return {
      ...editor,
      theme: foundTheme,
      availableThemes: [...editor.availableThemes.filter(theme => theme.id !== action.payload.id), editor.theme],
      rootCssVariables: websiteBuilderGenerateRootCssVariables(foundTheme, editor.globalStyles, editor.fonts),
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.UPDATE_GLOBAL_STYLES) {
    return {
      ...editor,
      globalStyles: action.payload.globalStyles,
      rootCssVariables: websiteBuilderGenerateRootCssVariables(editor.theme, action.payload.globalStyles, editor.fonts),
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.ADD_ICON_ASSETS) {
    const iconAssets = uniqBy([...editor.iconAssets, ...action.payload.iconAssets], asset => (
      JSON.stringify([asset.provider_code, asset.version_code, asset.variant_code, asset.asset_code])
    ));

    if (iconAssets.length === editor.iconAssets.length) {
      return editor;
    }

    return {
      ...editor,
      iconAssets,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.POST_MEDIA_ITEM) {
    return {
      ...editor,
      media: action.payload.media,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.ARCHIVE_MEDIA_ITEM) {
    return {
      ...editor,
      media: action.payload.media,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.RESTORE_MEDIA_ITEM) {
    return {
      ...editor,
      media: action.payload.media,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.ADD_FORM) {
    return {
      ...editor,
      forms: [...editor.forms, action.payload.form],
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.UPDATE_FORM) {
    return {
      ...editor,
      forms: editor.forms.map(form => form.id === action.payload.form.id ? action.payload.form : form),
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.UPDATE_FORM_NAME) {
    return {
      ...editor,
      forms: editor.forms.map(form => form.id === action.payload.form.id ? action.payload.form : form),
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.ARCHIVE_FORM) {
    return {
      ...editor,
      forms: editor.forms.filter(form => form.id !== action.payload.id),
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.SELECT_NODE) {
    return {
      ...editor,
      selectedNode: action.payload.selectedNode,
    };
  }

  if (action.type === WebsiteBuilderEditorActionType.UPDATE_SELECTED_NODE) {
    if (editor.selectedNode?.type === "element" && action.payload.selectedNodeUpdates?.type === "element") {
      return {
        ...editor,
        selectedNode: {
          ...editor.selectedNode,
          ...action.payload.selectedNodeUpdates,
        },
      };
    } else if (editor.selectedNode?.type === "container" && action.payload.selectedNodeUpdates?.type === "container") {
      return {
        ...editor,
        selectedNode: {
          ...editor.selectedNode,
          ...action.payload.selectedNodeUpdates,
        },
      };
    } else if (editor.selectedNode?.type === "section" && action.payload.selectedNodeUpdates?.type === "section") {
      return {
        ...editor,
        selectedNode: {
          ...editor.selectedNode,
          ...action.payload.selectedNodeUpdates,
        },
      };
    }

    return editor;
  }

  const root = editor.pageMap.get(editor.activePageId)?.root;

  if (
    editor.header == null ||
    root == null ||
    editor.footer == null ||
    !isWebsiteBuilderEditorHistoryAction(action)
  ) {
    return editor;
  }

  const updatedEditor = handleHistoryAction(editor, root, action);

  const updatedEditorPageMap = generatePageMap(updatedEditor.pages);

  const updatedEditorRoot = updatedEditorPageMap.get(updatedEditor.activePageId)?.root;
  const updatedEditorActivePageNodeMap = generateActivePageNodeMap(updatedEditor.header, updatedEditorRoot, updatedEditor.footer);

  return {
    ...updatedEditor,
    pageMap: updatedEditorPageMap,
    activePageNodeMap: updatedEditorActivePageNodeMap,
  };
}